import type {
  Product,
  ProductSource,
  Placement,
  ProductBrand,
  Article,
  PaginatedResponse,
  StatsResponse,
  ProductStatsResponse,
} from "@/types";
import type { ArticleStatus } from "@/types/enums";
import { getEnv } from "@trillion/ui/lib/env";
import { createApiClient } from "@trillion/ui/lib/api-client";

const api = createApiClient(() => getEnv("HELP_API_URL"));

interface ProductListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  category?: string;
  sourceId?: number;
  brandId?: number;
  catalogId?: string;
  itemGroupId?: string;
  sortBy?: string; // whitelisted server-side: name | price | rating | created_date
  sortDesc?: boolean;
}

interface SourceListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string; // whitelisted server-side: code | name | created_date
  sortDesc?: boolean;
}

interface PlacementListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string; // whitelisted server-side: zone | slot | label | created_date
  sortDesc?: boolean;
}

interface BrandListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sourceId?: number;
  sortBy?: string; // whitelisted server-side: name | external_id | created_date
  sortDesc?: boolean;
}

interface ArticleListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  category?: string;
  articleStatusId?: ArticleStatus;
  sortBy?: string;
  sortDesc?: boolean;
}

function toQuery(params: object): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") sp.set(key, String(value));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export const adminApi = {
  products: {
    async list(params: ProductListParams = {}): Promise<PaginatedResponse<Product>> {
      return api.request<PaginatedResponse<Product>>(`/admin/products${toQuery(params)}`);
    },
    async get(id: number): Promise<Product> {
      return api.request<Product>(`/admin/products/${id}`);
    },
    async create(data: Partial<Product>): Promise<Product> {
      return api.request<Product>(`/admin/products`, { method: "POST", body: JSON.stringify(data) });
    },
    async update(id: number, data: Partial<Product>): Promise<void> {
      return api.request<void>(`/admin/products/${id}`, { method: "PUT", body: JSON.stringify(data) });
    },
    async delete(id: number): Promise<void> {
      return api.request<void>(`/admin/products/${id}`, { method: "DELETE" });
    },
    async stats(catalogIds?: string[]): Promise<ProductStatsResponse> {
      const sp = new URLSearchParams();
      for (const c of catalogIds ?? []) sp.append("catalogId", c);
      const qs = sp.toString();
      return api.request<ProductStatsResponse>(`/admin/products/stats${qs ? `?${qs}` : ""}`);
    },
  },

  sources: {
    async list(params: SourceListParams = {}): Promise<PaginatedResponse<ProductSource>> {
      return api.request<PaginatedResponse<ProductSource>>(
        `/admin/product-sources${toQuery(params)}`
      );
    },
    async get(id: number): Promise<ProductSource> {
      return api.request<ProductSource>(`/admin/product-sources/${id}`);
    },
    async create(data: Partial<ProductSource>): Promise<ProductSource> {
      return api.request<ProductSource>(`/admin/product-sources`, {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    async update(id: number, data: Partial<ProductSource>): Promise<void> {
      return api.request<void>(`/admin/product-sources/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    async delete(id: number): Promise<void> {
      return api.request<void>(`/admin/product-sources/${id}`, { method: "DELETE" });
    },
  },

  placements: {
    async list(params: PlacementListParams = {}): Promise<PaginatedResponse<Placement>> {
      return api.request<PaginatedResponse<Placement>>(
        `/admin/placements${toQuery(params)}`
      );
    },
    async get(id: number): Promise<Placement> {
      return api.request<Placement>(`/admin/placements/${id}`);
    },
    async create(data: Partial<Placement>): Promise<Placement> {
      return api.request<Placement>(`/admin/placements`, {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    async update(id: number, data: Partial<Placement>): Promise<void> {
      return api.request<void>(`/admin/placements/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    async delete(id: number): Promise<void> {
      return api.request<void>(`/admin/placements/${id}`, { method: "DELETE" });
    },
  },

  brands: {
    async list(params: BrandListParams = {}): Promise<PaginatedResponse<ProductBrand>> {
      return api.request<PaginatedResponse<ProductBrand>>(
        `/admin/product-brands${toQuery(params)}`
      );
    },
    async get(id: number): Promise<ProductBrand> {
      return api.request<ProductBrand>(`/admin/product-brands/${id}`);
    },
    async create(data: Partial<ProductBrand>): Promise<ProductBrand> {
      return api.request<ProductBrand>(`/admin/product-brands`, {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    async update(id: number, data: Partial<ProductBrand>): Promise<void> {
      return api.request<void>(`/admin/product-brands/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    async delete(id: number): Promise<void> {
      return api.request<void>(`/admin/product-brands/${id}`, { method: "DELETE" });
    },
  },

  articles: {
    async list(params: ArticleListParams = {}): Promise<PaginatedResponse<Article>> {
      return api.request<PaginatedResponse<Article>>(`/admin/articles${toQuery(params)}`);
    },
    async get(id: number): Promise<Article> {
      return api.request<Article>(`/admin/articles/${id}`);
    },
    async create(data: Partial<Article>): Promise<Article> {
      return api.request<Article>(`/admin/articles`, { method: "POST", body: JSON.stringify(data) });
    },
    async update(id: number, data: Partial<Article>): Promise<void> {
      return api.request<void>(`/admin/articles/${id}`, { method: "PUT", body: JSON.stringify(data) });
    },
    async delete(id: number): Promise<void> {
      return api.request<void>(`/admin/articles/${id}`, { method: "DELETE" });
    },
    async stats(): Promise<StatsResponse> {
      return api.request<StatsResponse>(`/admin/articles/stats`);
    },
  },
};
